import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CoachGraphsView } from "@care-by-ella/contracts";
import { fetchCoachGraphs } from "@/lib/checkin-trend-api";
import { UnauthorizedError } from "@/lib/clinician-api";
import { MeasurementCharts } from "./MeasurementCharts";
import { WellbeingSeriesList } from "./WellbeingSeriesPanel";

// TES-1482 (TES-1458 §5, Ö-715) — the coach's wellbeing and weight graphs.
//
// A-01: "Coachen ska se 8–12 veckors separata trendgrafer för övergripande mående 1–5 och frivilligt registrerad vikt. Graferna får inte kombineras till en score eller medicinsk slutsats."
//
// Drawn from the series the patient and the doctor read, with the same
// components the doctor's page uses (WellbeingSeriesList, MeasurementCharts),
// so a point sits where it sits for them. What she may see is the server's
// decision: the payload holds only the series the register grants the
// coach, inside the window. Nothing here can widen it.

type State =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ok"; view: CoachGraphsView };

function Card({
  title,
  windowWeeks,
  children,
}: {
  title: string;
  windowWeeks: number;
  children: ReactNode;
}) {
  return (
    <article className="rounded-sm border border-surfaceMuted p-sm">
      <h3 className="text-sm font-medium text-textPrimary">{title}</h3>
      <p className="mb-xs text-xs text-textSecondary">
        Senaste {windowWeeks} veckorna
      </p>
      {children}
    </article>
  );
}

export function CoachGraphs({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  const [result, setResult] = useState<State>({ state: "loading" });
  // In a ref for the reason WeeklyCheckinTrends gives: an inline callback
  // must not refetch a PDL-logged route on every render.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    setResult({ state: "loading" });
    fetchCoachGraphs(patientId)
      .then((view) => {
        if (live) setResult({ state: "ok", view });
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current?.();
          return;
        }
        setResult({ state: "error" });
      });
    return () => {
      live = false;
    };
  }, [patientId]);

  if (result.state === "loading") {
    return <p className="text-xs text-textSecondary">Läser mående …</p>;
  }
  if (result.state === "error") {
    return (
      <p className="text-xs text-textSecondary">
        Måendet kunde inte läsas. Ladda om sidan.
      </p>
    );
  }
  const { view } = result;
  const empty = `Inga svar de senaste ${view.windowWeeks} veckorna.`;
  const weights = view.measurements.filter((m) => m.type === "weight");
  return (
    <div className="grid gap-sm md:grid-cols-2" data-testid="coach-graphs">
      <Card title="Övergripande mående (1–5)" windowWeeks={view.windowWeeks}>
        <WellbeingSeriesList view={view.wellbeing} empty={empty} />
      </Card>
      <Card title="Vikt" windowWeeks={view.windowWeeks}>
        {weights.length === 0 ? (
          <p className="text-sm text-textSecondary">{empty}</p>
        ) : (
          <MeasurementCharts measurements={weights} />
        )}
      </Card>
    </div>
  );
}
